import React, { useState, useCallback } from 'react';
import { Trash2, Eye, Play, Settings } from 'lucide-react';

interface PatternItem {
  id: string;
  emoji: string;
  color: string;
  type: 'shape' | 'color' | 'size';
}

interface PatternMakerConfig {
  name: string;
  question: string;
  pattern: PatternItem[];
  options: PatternItem[];
  allowInteraction: boolean;
  showHint: boolean;
  maxLength: number;
}

interface PatternMakerConfigurationFormProps {
  config: PatternMakerConfig;
  onChange: (config: PatternMakerConfig) => void;
}

const PatternMakerConfigurationForm: React.FC<PatternMakerConfigurationFormProps> = ({ config, onChange }) => {
  const [localConfig, setLocalConfig] = useState<PatternMakerConfig>(config);

  // Common emojis for patterns
  const emojiSuggestions = [
    '🔴', '🔵', '🟡', '🟢', '🟣', '🟠', '⚫', '⚪',
    '🔺', '🔻', '🔸', '🔹', '🔶', '🔷', '🔳', '🔲',
    '⭐', '🌟', '💫', '✨', '🎯', '🎪', '🎨', '🎭',
    '🍎', '🍊', '🍋', '🍌', '🍇', '🍓', '🍒', '🍑',
    '🐱', '🐶', '🐰', '🐸', '🐯', '🐻', '🐼', '🐨',
    '🚗', '🚕', '🚙', '🚌', '🚎', '🏎️', '🚓', '🚑',
    '⚽', '🏀', '🏈', '⚾', '🎾', '🏐', '🏉', '🎱',
    '📚', '📖', '📝', '✏️', '🖊️', '🖍️', '📏', '📐'
  ];

  const updateConfig = useCallback((updates: Partial<PatternMakerConfig>) => {
    const newConfig = { ...localConfig, ...updates };
    setLocalConfig(newConfig);
    onChange(newConfig);
  }, [localConfig, onChange]);

  const addPatternItem = useCallback((emoji: string) => {
    const newItem: PatternItem = {
      id: `pattern_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      emoji,
      color: '#3B82F6',
      type: 'shape'
    };
    
    updateConfig({
      pattern: [...localConfig.pattern, newItem]
    });
  }, [localConfig.pattern, updateConfig]);

  const removePatternItem = useCallback((index: number) => {
    const newPattern = localConfig.pattern.filter((_, i) => i !== index);
    updateConfig({ pattern: newPattern });
  }, [localConfig.pattern, updateConfig]);

  const addOptionItem = useCallback((emoji: string) => {
    const newItem: PatternItem = {
      id: `option_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      emoji,
      color: '#3B82F6',
      type: 'shape'
    };
    
    updateConfig({
      options: [...localConfig.options, newItem]
    });
  }, [localConfig.options, updateConfig]);

  const removeOptionItem = useCallback((index: number) => {
    const newOptions = localConfig.options.filter((_, i) => i !== index);
    updateConfig({ options: newOptions });
  }, [localConfig.options, updateConfig]);

  const addQuickPattern = useCallback((pattern: string[]) => {
    const newItems: PatternItem[] = pattern.map((emoji, index) => ({
      id: `quick_pattern_${index}_${Date.now()}`,
      emoji,
      color: '#3B82F6',
      type: 'shape'
    }));
    
    updateConfig({ pattern: newItems });
  }, [updateConfig]);

  const addQuickOptions = useCallback((options: string[]) => {
    const newItems: PatternItem[] = options.map((emoji, index) => ({
      id: `quick_option_${index}_${Date.now()}`,
      emoji,
      color: '#3B82F6',
      type: 'shape'
    }));
    
    updateConfig({ options: newItems });
  }, [updateConfig]);

  return (
    <div className="pattern-maker-config-form space-y-6">
      {/* Basic Settings */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
          <Settings className="w-5 h-5" />
          Configuration du Créateur de Rythmes
        </h3>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Nom de la ressource
          </label>
          <input
            type="text"
            value={localConfig.name}
            onChange={(e) => updateConfig({ name: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Créateur de Rythmes"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Question/Consigne
          </label>
          <textarea
            value={localConfig.question}
            onChange={(e) => updateConfig({ question: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            rows={3}
            placeholder="Reproduisez le rythme affiché en utilisant les éléments disponibles"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Longueur maximale
            </label>
            <input
              type="number"
              min="1"
              max="20"
              value={localConfig.maxLength}
              onChange={(e) => updateConfig({ maxLength: parseInt(e.target.value) || 1 })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex gap-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={localConfig.allowInteraction}
              onChange={(e) => updateConfig({ allowInteraction: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Permettre l'interaction</span>
          </label>
          
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={localConfig.showHint}
              onChange={(e) => updateConfig({ showHint: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm text-gray-700">Afficher les astuces</span>
          </label>
        </div>
      </div>

      {/* Pattern Configuration */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-semibold text-gray-800 flex items-center gap-2">
            <Play className="w-4 h-4" />
            Rythme à reproduire
          </h4>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => addQuickPattern(['🔴', '🔵', '🔴', '🔵'])}
              className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
            >
              Rouge-Bleu
            </button>
            <button
              type="button"
              onClick={() => addQuickPattern(['⭐', '🌟', '⭐', '🌟'])}
              className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
            >
              Étoiles
            </button>
            <button
              type="button"
              onClick={() => addQuickPattern(['🍎', '🍊', '🍎', '🍊'])}
              className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
            >
              Fruits
            </button>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex gap-2 flex-wrap mb-3">
            {localConfig.pattern.map((item, index) => (
              <div key={item.id} className="relative">
                <div className="w-12 h-12 flex items-center justify-center text-2xl bg-white rounded-lg shadow-sm border-2 border-blue-300">
                  {item.emoji}
                </div>
                <button
                  type="button"
                  onClick={() => removePatternItem(index)}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          
          <div className="text-sm text-gray-600 mb-3">
            Cliquez sur un emoji pour l'ajouter au rythme :
          </div>
          <div className="flex gap-1 flex-wrap">
            {emojiSuggestions.slice(0, 16).map((emoji) => (
              <button
                type="button"
                key={emoji}
                onClick={() => addPatternItem(emoji)}
                className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-200 rounded"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Options Configuration */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h4 className="text-md font-semibold text-gray-800 flex items-center gap-2">
            <Eye className="w-4 h-4" />
            Éléments disponibles
          </h4>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => addQuickOptions(['🔴', '🔵', '🟡', '🟢'])}
              className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
            >
              Couleurs
            </button>
            <button
              type="button"
              onClick={() => addQuickOptions(['⭐', '🌟', '💫', '✨'])}
              className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
            >
              Étoiles
            </button>
            <button
              type="button"
              onClick={() => addQuickOptions(['🍎', '🍊', '🍋', '🍌'])}
              className="px-3 py-1 text-xs bg-orange-100 text-orange-700 rounded hover:bg-orange-200"
            >
              Fruits
            </button>
          </div>
        </div>

        <div className="p-4 bg-gray-50 rounded-lg">
          <div className="flex gap-2 flex-wrap mb-3">
            {localConfig.options.map((item, index) => (
              <div key={item.id} className="relative">
                <div className="w-12 h-12 flex items-center justify-center text-2xl bg-white rounded-lg shadow-sm border-2 border-gray-300">
                  {item.emoji}
                </div>
                <button
                  type="button"
                  onClick={() => removeOptionItem(index)}
                  className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          
          <div className="text-sm text-gray-600 mb-3">
            Cliquez sur un emoji pour l'ajouter aux options :
          </div>
          <div className="flex gap-1 flex-wrap">
            {emojiSuggestions.map((emoji) => (
              <button
                type="button"
                key={emoji}
                onClick={() => addOptionItem(emoji)}
                className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-200 rounded"
              >
                {emoji}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="space-y-4">
        <h4 className="text-md font-semibold text-gray-800 flex items-center gap-2">
          <Eye className="w-4 h-4" />
          Aperçu
        </h4>
        
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="text-sm text-blue-800 mb-2">
            <strong>Rythme à reproduire :</strong>
          </div>
          <div className="flex gap-2 mb-4">
            {localConfig.pattern.map((item, index) => (
              <div key={item.id} className="w-10 h-10 flex items-center justify-center text-xl bg-white rounded border-2 border-blue-300">
                {item.emoji}
              </div>
            ))}
          </div>
          
          <div className="text-sm text-blue-800 mb-2">
            <strong>Éléments disponibles :</strong>
          </div>
          <div className="flex gap-2">
            {localConfig.options.map((item, index) => (
              <div key={item.id} className="w-10 h-10 flex items-center justify-center text-xl bg-white rounded border-2 border-gray-300">
                {item.emoji}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatternMakerConfigurationForm;
